import { useReveal, useInView, useCounter } from "../hooks/useReveal";
import { TrendingUp, Home, Sparkles, MapPin } from "lucide-react";

const MARKET_STATS = [
  {
    icon: TrendingUp,
    value: 74.7,
    suffix: " Bn",
    prefix: "$",
    decimals: 1,
    label: "India Market by 2034",
    caption: "From USD 36.89 Bn in 2025",
  },
  {
    icon: Home,
    value: 60,
    suffix: "%",
    label: "Residential Share",
    caption: "Of India’s design demand in 2025",
  },
  {
    icon: Sparkles,
    value: 37.8,
    suffix: "% YoY",
    decimals: 1,
    label: "Luxury Housing Growth",
    caption: "Sales momentum, 9M 2024",
  },
  {
    icon: MapPin,
    value: 16.47,
    suffix: "% CAGR",
    decimals: 2,
    label: "Residential Interiors",
    caption: "India projection to 2031",
  },
];

const HERO_IMAGE = "/varnella-market.png";

function AnimatedNumber({ value, prefix = "", suffix = "", decimals = 0, start }) {
  const n = useCounter(value * Math.pow(10, decimals), 1800, start);
  const display = (n / Math.pow(10, decimals)).toFixed(decimals);
  return (
    <span>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}

export default function MarketCase() {
  const ref = useReveal();
  const [statRef, statInView] = useInView(0.25);

  return (
    <section
      id="market"
      data-testid="market-section"
      className="relative py-24 lg:py-40 bg-[#1C1917] text-white overflow-hidden"
    >
      <div ref={ref} className="reveal max-w-[90rem] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        {/* Left column — narrative */}
        <div className="lg:col-span-6">
          <p className="overline text-stone-400 mb-6">Market Case · India 2026</p>
          <h2
            data-testid="market-headline"
            className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-white"
          >
            An industry rewriting how India
            <em className="italic font-normal text-white/70"> lives, hosts, heals</em>
            <span> and shops.</span>
          </h2>

          <div className="mt-10 space-y-5 text-stone-300 leading-relaxed max-w-xl">
            <p>
              India’s interior design market has crossed <span className="text-white font-medium">USD 36.89 billion</span> in 2025 and is projected
              to nearly double to <span className="text-white font-medium">USD 74.73 billion</span> by 2034 —
              growing at a steady <span className="text-white font-medium">8.16% CAGR</span>.
            </p>
            <p>
              Residential owners already drive <span className="text-white font-medium">60% of demand</span>, and the
              luxury home segment alone posted <span className="text-white font-medium">37.8% year-on-year</span> growth
              through the first nine months of 2024. India&rsquo;s residential interiors category is
              forecast to expand at <span className="text-white font-medium">16.47% CAGR</span> through 2031 — one of
              the fastest anywhere in the world.
            </p>
            <p className="text-stone-400">
              The tailwinds — urban migration, rising disposable incomes, developer-finished
              handovers, and the Instagram-native homeowner — are all pointing in one direction:
              interiors that are personal, considered and unmistakably yours.
            </p>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-5">
            <a
              href="#contact"
              data-testid="market-cta-primary"
              className="btn-ghost-light bg-white text-[#1C1917] border-white hover:bg-transparent hover:text-white"
            >
              Start Your Project
            </a>
            <span className="text-[11px] tracking-[0.25em] uppercase text-stone-500">
              Sources · IMARC · Mordor Intelligence · Credence Research
            </span>
          </div>
        </div>

        {/* Right column — image + stat grid */}
        <div className="lg:col-span-6">
          <div className="img-zoom relative rounded-sm overflow-hidden aspect-[4/5] mb-8">
            <img
              src={HERO_IMAGE}
              alt="A contemporary Indian luxury residential interior with bespoke surfaces"
              loading="lazy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <p className="text-[11px] tracking-[0.3em] uppercase text-white/70 mb-2">
                Case in Point
              </p>
              <h3 className="font-serif-display text-2xl text-white leading-snug max-w-md">
                The Indian home is no longer decorated. It is authored.
              </h3>
            </div>
          </div>

          <div
            ref={statRef}
            data-testid="market-stats"
            className="grid grid-cols-2 gap-x-8 gap-y-10"
          >
            {MARKET_STATS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.label}
                  data-testid={`market-stat-${i}`}
                  className="flex flex-col border-t border-white/20 pt-6"
                >
                  <span className="w-10 h-10 rounded-sm border border-white/25 flex items-center justify-center text-white/80 mb-4">
                    <Icon size={16} strokeWidth={1.4} />
                  </span>
                  <p className="font-serif-display text-4xl lg:text-5xl leading-none text-white">
                    <AnimatedNumber
                      value={s.value}
                      prefix={s.prefix}
                      suffix={s.suffix}
                      decimals={s.decimals || 0}
                      start={statInView}
                    />
                  </p>
                  <p className="mt-3 text-[11px] tracking-[0.25em] uppercase text-white/70">
                    {s.label}
                  </p>
                  <p className="mt-1 text-[13px] text-stone-400 leading-relaxed">
                    {s.caption}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
